/**
 * 도시(CITIES)에 속하지 않는 넓은 지명 — 상위 도시가 없거나 도시보다 넓은 곳(10-19).
 *
 * 왜 따로 두나: SEARCH_PLACES 는 소속 도시(cityId)가 필수라, 가장 가까운 도시가 멀거나 도시보다
 * 넓은 곳(대만의 현)을 넣으면 그 도시 이름으로 검색된다. 이 목록은 도시 없이 **좌표 박스**로만
 * 검색한다 — 검색바가 `scope=keyword` + 박스 + `place=<id>`(전 세계 명소와 같은 URL 모양)로
 * 보내므로 새 랜딩·색인 URL 이 생기지 않는다.
 *
 * 이름은 5개 언어를 직접 적는다(번역 API 금지). `aliases` 는 흔한 다른 표기 — 정규화 후
 * 토큰 완전일치·부분 일치(자동완성) 양쪽에 쓰인다. 같은 이름의 도시(CITIES)가 있으면 짧은
 * 이름은 도시 몫이다 — 여기엔 "~현" 처럼 구분되는 표기만 넣는다("화롄" = 도시, "화롄현" = 현).
 *
 * 미야코지마·이시가키는 10-19 도시로 올렸다(CITIES JP-MMY·JP-ISG) — 예전 id 는
 * LEGACY_AREA_CITY_IDS 로 계속 풀린다(같은 날 도시가 된 수기 지명 id 도 함께).
 */
import type { Bounds, LocalizedPlaceName } from "@/config/locations";

export interface SearchArea {
  /** "area:<국가>-<이름>" — 검색 URL ?place= 값 */
  id: string;
  countryCode: string;
  name: Required<LocalizedPlaceName>;
  aliases: string[];
  coordinates: { latitude: number; longitude: number };
  /** 검색 박스 반폭(km). bounds 가 있으면 그쪽이 우선 */
  radiusKm: number;
  /** 길쭉한 현처럼 정사각형이 맞지 않는 곳의 검색 박스(대략의 행정 경계) */
  bounds?: Bounds;
  /** 이 지명이 곧 지역(admin1) 하나일 때 그 코드(ISO 3166-2) — TW_REGIONS 참고 */
  regionCode?: string;
}

/** [남, 서, 북, 동] → Bounds */
const box = (s: number, w: number, n: number, e: number): Bounds => ({
  sw_lat: s,
  sw_lng: w,
  ne_lat: n,
  ne_lng: e,
});

export const SEARCH_AREAS: SearchArea[] = [
  // ===== 대만 — 현(縣). 직할시·시(市)는 도시(CITIES)가 대신한다(TW_REGIONS) =====
  {
    id: "area:TW-HSQ",
    countryCode: "TW",
    regionCode: "TW-HSQ",
    name: { ko: "신주현", en: "Hsinchu County", ja: "新竹県", "zh-CN": "新竹县", "zh-TW": "新竹縣" },
    aliases: ["신주 현", "hsinchu-county", "주베이", "竹北", "zhubei"],
    coordinates: { latitude: 24.7, longitude: 121.15 },
    radiusKm: 25,
    bounds: box(24.43, 120.93, 24.95, 121.42),
  },
  {
    id: "area:TW-MIA",
    countryCode: "TW",
    regionCode: "TW-MIA",
    name: { ko: "먀오리현", en: "Miaoli County", ja: "苗栗県", "zh-CN": "苗栗县", "zh-TW": "苗栗縣" },
    aliases: ["먀오리", "묘율", "miaoli", "苗栗"],
    coordinates: { latitude: 24.5, longitude: 120.94 },
    radiusKm: 28,
    bounds: box(24.28, 120.62, 24.72, 121.26),
  },
  {
    id: "area:TW-CHA",
    countryCode: "TW",
    regionCode: "TW-CHA",
    name: { ko: "장화현", en: "Changhua County", ja: "彰化県", "zh-CN": "彰化县", "zh-TW": "彰化縣" },
    aliases: ["장화", "창화", "changhua", "彰化", "彰化市"],
    coordinates: { latitude: 23.99, longitude: 120.47 },
    radiusKm: 22,
    bounds: box(23.78, 120.23, 24.21, 120.7),
  },
  {
    id: "area:TW-NAN",
    countryCode: "TW",
    regionCode: "TW-NAN",
    name: { ko: "난터우현", en: "Nantou County", ja: "南投県", "zh-CN": "南投县", "zh-TW": "南投縣" },
    aliases: ["난터우", "난토우", "nantou", "南投", "칭징농장", "清境農場", "清境农场", "cingjing"],
    coordinates: { latitude: 23.84, longitude: 120.98 },
    radiusKm: 40,
    bounds: box(23.44, 120.62, 24.25, 121.35),
  },
  {
    id: "area:TW-YUN",
    countryCode: "TW",
    regionCode: "TW-YUN",
    name: { ko: "윈린현", en: "Yunlin County", ja: "雲林県", "zh-CN": "云林县", "zh-TW": "雲林縣" },
    aliases: ["윈린", "yunlin", "雲林", "云林"],
    coordinates: { latitude: 23.7, longitude: 120.42 },
    radiusKm: 22,
    bounds: box(23.5, 120.13, 23.85, 120.75),
  },
  {
    id: "area:TW-CYQ",
    countryCode: "TW",
    regionCode: "TW-CYQ",
    name: { ko: "자이현", en: "Chiayi County", ja: "嘉義県", "zh-CN": "嘉义县", "zh-TW": "嘉義縣" },
    aliases: ["자이 현", "chiayi-county"],
    coordinates: { latitude: 23.46, longitude: 120.55 },
    radiusKm: 35,
    bounds: box(23.3, 120.1, 23.63, 120.95),
  },
  {
    // 아리산 — 자이현 산간(자이 시내에서 40km). 도시 박스로는 닿지 않는다.
    id: "area:TW-ALISHAN",
    countryCode: "TW",
    name: { ko: "아리산", en: "Alishan", ja: "阿里山", "zh-CN": "阿里山", "zh-TW": "阿里山" },
    aliases: ["알리산", "아리산 국가풍경구", "ali mountain", "阿里山國家森林遊樂區", "阿里山国家森林游乐区"],
    coordinates: { latitude: 23.51, longitude: 120.8 },
    radiusKm: 10,
  },
  {
    id: "area:TW-PIF",
    countryCode: "TW",
    regionCode: "TW-PIF",
    name: { ko: "핑둥현", en: "Pingtung County", ja: "屏東県", "zh-CN": "屏东县", "zh-TW": "屏東縣" },
    // 샤오류추(小琉球)는 10-19 여행지(CITIES TW-XLQ)
    aliases: ["핑둥", "핑동", "pingtung", "屏東", "屏东", "屏東市"],
    coordinates: { latitude: 22.4, longitude: 120.62 },
    radiusKm: 50,
    // 남북으로 긴 현(헝춘반도 끝 21.9 ~ 22.9) — 서쪽 경계를 120.35 로 잘라 가오슝 시내를 뺀다.
    bounds: box(21.89, 120.35, 22.9, 120.92),
  },
  {
    id: "area:TW-ILA",
    countryCode: "TW",
    regionCode: "TW-ILA",
    name: { ko: "이란현", en: "Yilan County", ja: "宜蘭県", "zh-CN": "宜兰县", "zh-TW": "宜蘭縣" },
    aliases: ["이란 현", "yilan-county", "ilan county", "뤄둥", "羅東", "罗东", "luodong"],
    coordinates: { latitude: 24.65, longitude: 121.64 },
    radiusKm: 35,
    bounds: box(24.3, 121.3, 25.0, 121.98),
  },
  {
    id: "area:TW-HUA",
    countryCode: "TW",
    regionCode: "TW-HUA",
    name: { ko: "화롄현", en: "Hualien County", ja: "花蓮県", "zh-CN": "花莲县", "zh-TW": "花蓮縣" },
    aliases: ["화롄 현", "화련현", "hualien-county"],
    coordinates: { latitude: 23.74, longitude: 121.35 },
    radiusKm: 60,
    bounds: box(23.09, 121.0, 24.38, 121.7),
  },
  {
    id: "area:TW-TTT",
    countryCode: "TW",
    regionCode: "TW-TTT",
    name: { ko: "타이둥현", en: "Taitung County", ja: "台東県", "zh-CN": "台东县", "zh-TW": "臺東縣" },
    // 뤼다오·란위는 10-19 여행지(CITIES TW-GRI·TW-OKY)
    aliases: ["타이둥 현", "台東縣", "taitung-county"],
    coordinates: { latitude: 22.75, longitude: 121.15 },
    radiusKm: 60,
    // 뤼다오(22.66)·란위(22.05) 포함.
    bounds: box(21.93, 120.73, 23.45, 121.62),
  },
  {
    id: "area:TW-PEN",
    countryCode: "TW",
    regionCode: "TW-PEN",
    name: { ko: "펑후", en: "Penghu", ja: "澎湖", "zh-CN": "澎湖", "zh-TW": "澎湖" },
    aliases: ["펑후현", "펑후섬", "펑후 제도", "penghu county", "penghu islands", "澎湖県", "澎湖县", "澎湖縣", "澎湖諸島", "澎湖群島", "마궁", "馬公", "马公", "magong"],
    coordinates: { latitude: 23.57, longitude: 119.58 },
    radiusKm: 30,
    bounds: box(23.15, 119.3, 23.8, 119.75),
  },
  {
    id: "area:TW-KIN",
    countryCode: "TW",
    regionCode: "TW-KIN",
    name: { ko: "진먼", en: "Kinmen", ja: "金門", "zh-CN": "金门", "zh-TW": "金門" },
    aliases: ["진먼현", "진먼섬", "kinmen county", "kinmen island", "金門県", "金门县", "金門縣", "金門島", "金门岛"],
    coordinates: { latitude: 24.45, longitude: 118.35 },
    radiusKm: 18,
    bounds: box(24.37, 118.13, 24.55, 118.5),
  },
  {
    id: "area:TW-LIE",
    countryCode: "TW",
    regionCode: "TW-LIE",
    name: { ko: "마쭈", en: "Matsu", ja: "馬祖", "zh-CN": "马祖", "zh-TW": "馬祖" },
    aliases: ["마쭈열도", "마조", "롄장현", "롄장", "matsu islands", "lienchiang", "lienchiang county", "馬祖列島", "马祖列岛", "連江県", "连江县", "連江縣"],
    coordinates: { latitude: 26.16, longitude: 120.2 },
    radiusKm: 30,
    bounds: box(25.93, 119.88, 26.4, 120.52),
  },
];

/**
 * 예전 지명 id → 지금 도시 id. 0a7d983f(미배포)가 섬을 `area:` 로 만들었다가 10-19 도시로
 * 올렸다 — 최근 검색·공유 URL 의 `?place=area:JP-MIYAKOJIMA` 가 계속 풀리게 둔다.
 */
export const LEGACY_AREA_CITY_IDS: Record<string, string> = {
  "area:JP-MIYAKOJIMA": "JP-MMY",
  "area:JP-ISHIGAKI": "JP-ISG",
  // 10-19 여행지 확장 — 엉뚱한 도시(하코네→요코하마·강릉→서울…)에 붙어 있던 수기 지명이 도시가
  // 됐다(config/destinations.generated). 예전 `?place=` 링크는 그 도시로 풀린다.
  "JP-TYO-NARITA": "JP-NRT",
  "JP-KAWASAKI": "JP-KWS",
  "JP-HIMEJI": "JP-HMJ",
  "JP-MIYAJIMA": "JP-ITK",
  "JP-TAKAYAMA": "JP-TKY",
  "JP-SHIRAKAWAGO": "JP-SRK",
  "JP-NIKKO": "JP-NIK",
  "JP-KAMAKURA": "JP-KMK",
  "JP-HAKONE": "JP-HKN",
  "JP-ATAMI": "JP-ATM",
  "JP-KAWAGUCHIKO": "JP-FFL",
  "JP-BEPPU": "JP-BPU",
  "JP-YUFUIN": "JP-YFN",
  "JP-OTARU": "JP-OTR",
  "JP-FURANO": "JP-FRN",
  "JP-BIEI": "JP-BIE",
  "JP-HAKODATE": "JP-HKD",
  "JP-NOBORIBETSU": "JP-NBT",
  "JP-ASAHIKAWA": "JP-AKJ",
  "JP-NISEKO": "JP-NSK",
  "KR-CHUNCHEON": "KR-CHC",
  "KR-GANGNEUNG": "KR-GNG",
  "KR-SOKCHO": "KR-SKC",
  "KR-YANGYANG": "KR-SKC",
  "KR-YEOSU": "KR-YSU",
  "KR-SUNCHEON": "KR-SCN",
  "KR-POHANG": "KR-POH",
  "KR-GYEONGJU": "KR-GYJ",
  "KR-ANDONG": "KR-AND",
  "KR-TONGYEONG": "KR-TYG",
  "KR-GEOJE": "KR-TYG",
  "KR-PYEONGCHANG": "KR-PYC",
  "KR-GAPYEONG": "KR-GPY",
  "TW-NWT-TAMSUI": "TW-TAM",
  "TW-YIL-JIAOXI": "TW-JXI",
  "TW-HUN-TAROKO": "TW-TRK",
};

/** 예전 id 면 지금 id, 아니면 그대로. */
export function canonicalPlaceId(id: string): string {
  return LEGACY_AREA_CITY_IDS[id] ?? id;
}

/**
 * 대만 행정구역 22곳(직할시 6·시 3·현 13) — ISO 3166-2:TW 코드 → 검색바 지명.
 * 직할시·시는 같은 이름의 도시(CITIES), 현은 위 SEARCH_AREAS 의 박스.
 *
 * slug 는 prod geo_admin1 에 넣을 값과 같다(admin docs/seo-data/prod-fixes-2026-10-19/
 * taiwan-admin1.sql). prod 의 대만 admin1 은 GeoNames 옛 구분(푸젠성·타이완성)이라 이 SQL 을
 * 적용하기 전엔 지역 검색이 대만 지점을 묶지 못한다.
 */
export const TW_REGIONS: ReadonlyArray<{ code: string; slug: string; placeId: string }> = [
  { code: "TW-TPE", slug: "taipei", placeId: "TW-TPE" },
  { code: "TW-NWT", slug: "new-taipei", placeId: "TW-NWT" },
  { code: "TW-TAO", slug: "taoyuan", placeId: "TW-TAO" },
  { code: "TW-TXG", slug: "taichung", placeId: "TW-TXG" },
  { code: "TW-TNN", slug: "tainan", placeId: "TW-TNN" },
  { code: "TW-KHH", slug: "kaohsiung", placeId: "TW-KHH" },
  { code: "TW-KEE", slug: "keelung", placeId: "TW-KEE" },
  { code: "TW-HSZ", slug: "hsinchu", placeId: "TW-HSZ" },
  { code: "TW-CYI", slug: "chiayi", placeId: "TW-CYI" },
  { code: "TW-HSQ", slug: "hsinchu-county", placeId: "area:TW-HSQ" },
  { code: "TW-MIA", slug: "miaoli", placeId: "area:TW-MIA" },
  { code: "TW-CHA", slug: "changhua", placeId: "area:TW-CHA" },
  { code: "TW-NAN", slug: "nantou", placeId: "area:TW-NAN" },
  { code: "TW-YUN", slug: "yunlin", placeId: "area:TW-YUN" },
  { code: "TW-CYQ", slug: "chiayi-county", placeId: "area:TW-CYQ" },
  { code: "TW-PIF", slug: "pingtung", placeId: "area:TW-PIF" },
  { code: "TW-ILA", slug: "yilan", placeId: "area:TW-ILA" },
  { code: "TW-HUA", slug: "hualien", placeId: "area:TW-HUA" },
  { code: "TW-TTT", slug: "taitung", placeId: "area:TW-TTT" },
  { code: "TW-PEN", slug: "penghu", placeId: "area:TW-PEN" },
  { code: "TW-KIN", slug: "kinmen", placeId: "area:TW-KIN" },
  { code: "TW-LIE", slug: "lienchiang", placeId: "area:TW-LIE" },
];

/**
 * 대만 현을 지역(admin1) 검색(`scope=region&region=TW-…`)으로 보낼지. false = 박스 검색.
 *
 * 지역 검색은 그 지역에 살아 있는 지점이 있어야 열린다(lib/seo/region-coverage). 지점이 없는
 * 지역 URL 은 나라 페이지로 308 되므로, ① taiwan-admin1.sql 을 prod 에 적용하고 ② 대만 지점이
 * 생겨 geo 재판정까지 끝난 뒤에 true 로 바꾼다. 그 전엔 박스 검색이 같은 범위를 보여 준다.
 * (직할시·시는 이 값과 무관하게 도시 검색이다 — 도시 랜딩이 대표.)
 */
export const TW_REGION_SEARCH_LIVE = false;

/**
 * 일본 도도부현 지명(SEARCH_PLACES 의 `JP-PREF-*`) → 지역(admin1) 코드. ISO 3166-2:JP 순서이며
 * prod geo_admin1.code 와 같다. 검색바에서 고르면 도도부현 전체 지역 검색
 * (`scope=region&region=JP-47`)으로 보낸다 — 예전엔 현청 소재지 둘레 3.3km 박스였다.
 */
export const JP_PREFECTURE_REGION_CODES: Record<string, string> = {
  "JP-PREF-HOKKAIDO": "JP-01",
  "JP-PREF-AOMORI": "JP-02",
  "JP-PREF-IWATE": "JP-03",
  "JP-PREF-MIYAGI": "JP-04",
  "JP-PREF-AKITA": "JP-05",
  "JP-PREF-YAMAGATA": "JP-06",
  "JP-PREF-FUKUSHIMA": "JP-07",
  "JP-PREF-IBARAKI": "JP-08",
  "JP-PREF-TOCHIGI": "JP-09",
  "JP-PREF-GUNMA": "JP-10",
  "JP-PREF-SAITAMA": "JP-11",
  "JP-PREF-CHIBA": "JP-12",
  "JP-PREF-TOKYO-TO": "JP-13",
  "JP-PREF-KANAGAWA": "JP-14",
  "JP-PREF-NIIGATA": "JP-15",
  "JP-PREF-TOYAMA": "JP-16",
  "JP-PREF-ISHIKAWA": "JP-17",
  "JP-PREF-FUKUI": "JP-18",
  "JP-PREF-YAMANASHI": "JP-19",
  "JP-PREF-NAGANO": "JP-20",
  "JP-PREF-GIFU": "JP-21",
  "JP-PREF-SHIZUOKA": "JP-22",
  "JP-PREF-AICHI": "JP-23",
  "JP-PREF-MIE": "JP-24",
  "JP-PREF-SHIGA": "JP-25",
  "JP-PREF-KYOTO-FU": "JP-26",
  "JP-PREF-OSAKA-FU": "JP-27",
  "JP-PREF-HYOGO": "JP-28",
  "JP-PREF-NARA": "JP-29",
  "JP-PREF-WAKAYAMA": "JP-30",
  "JP-PREF-TOTTORI": "JP-31",
  "JP-PREF-SHIMANE": "JP-32",
  "JP-PREF-OKAYAMA": "JP-33",
  "JP-PREF-HIROSHIMA": "JP-34",
  "JP-PREF-YAMAGUCHI": "JP-35",
  "JP-PREF-TOKUSHIMA": "JP-36",
  "JP-PREF-KAGAWA": "JP-37",
  "JP-PREF-EHIME": "JP-38",
  "JP-PREF-KOCHI": "JP-39",
  "JP-PREF-FUKUOKA-KEN": "JP-40",
  "JP-PREF-SAGA": "JP-41",
  "JP-PREF-NAGASAKI": "JP-42",
  "JP-PREF-KUMAMOTO": "JP-43",
  "JP-PREF-OITA": "JP-44",
  "JP-PREF-MIYAZAKI": "JP-45",
  "JP-PREF-KAGOSHIMA": "JP-46",
  "JP-PREF-OKINAWA": "JP-47",
};

/** "JP-PREF-FUKUOKA-KEN" → "fukuoka" (geo_admin1.slug 와 같은 규칙. 틀려도 정규 308 이 코드로 고친다). */
export function prefectureRegionSlug(placeId: string): string {
  return placeId
    .replace(/^JP-PREF-/, "")
    .replace(/-(KEN|FU|TO)$/, "")
    .toLowerCase();
}
